import { NgClass, UpperCasePipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink, RouterLinkActive } from '@angular/router';
import {
    ApplyPipe,
    FilledIconPipe,
    SeparatorComponent,
} from '@interacta-shared/ui';
import { IconComponent } from '@interacta-shared/ui-icons';
import {
    pinCommunity,
    selectPinnedCommunitiesIds,
} from '@interacta/data-access-community-tree';
import { ICommunity } from '@interacta/util-community';
import { SidebarWorkspace } from '@modules/sidebar/models/sidebar.model';
import { CacheMap, WorkspaceUI } from '@modules/state/models/ui-state.model';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { map, switchMap } from 'rxjs';
import { SidebarCommunityComponent } from '../sidebar-community/sidebar-community.component';
import { SidebarWorkspaceComponent } from '../sidebar-workspace/sidebar-workspace.component';

@Component({
    selector: 'interacta-sidebar-workspace-list',
    templateUrl: './sidebar-workspace-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        RouterLink,
        RouterLinkActive,
        MatTooltip,
        IconComponent,
        SeparatorComponent,
        SidebarCommunityComponent,
        SidebarWorkspaceComponent,
        TranslatePipe,
        ApplyPipe,
        UpperCasePipe,
        FilledIconPipe,
        NgClass,
    ],
})
export class SidebarWorkspaceListComponent {
    private readonly store = inject(Store);

    readonly workspaceList = input.required<SidebarWorkspace[]>();
    readonly selectedCommunity = input.required<ICommunity | null>();
    readonly uiStates = input.required<CacheMap<WorkspaceUI> | null>();
    readonly expanded = input(true);
    readonly redirectTo = input<'community' | 'create'>('community');
    readonly showDashboardLink = input(false);
    readonly pinEnabled = input(false);

    readonly collapsedWs = output<number>();

    readonly showMyCommunities = computed(() => {
        const workspaceList = this.workspaceList();

        if (workspaceList.length) {
            if (workspaceList.length === 1) {
                const commmunitiesVisibleInDashboard =
                    workspaceList[0].communities?.filter(
                        (c) => c.visibleInDashboard,
                    );

                return !commmunitiesVisibleInDashboard?.length ||
                    commmunitiesVisibleInDashboard?.length === 1
                    ? false
                    : true;
            } else {
                return true;
            }
        } else {
            return false;
        }
    });

    readonly pinnedCommunities = signal<ICommunity[]>([]);

    constructor() {
        toObservable(this.workspaceList)
            .pipe(
                switchMap((workspaceList) => {
                    return this.store.select(selectPinnedCommunitiesIds).pipe(
                        map((pinnedIds) => {
                            const allCommunities = workspaceList.flatMap(
                                (workspace) => workspace.communities ?? [],
                            );

                            return allCommunities
                                .map((community) => ({
                                    community,
                                    pinnedId: pinnedIds.find(
                                        (p) => p.id === community.id,
                                    ),
                                }))
                                .filter(({ pinnedId }) => pinnedId != null)
                                .map(({ community, pinnedId }) => ({
                                    ...community,
                                    pinnedTimestamp: pinnedId?.pinnedTimestamp,
                                }))
                                .sort(
                                    (a, b) =>
                                        (a.pinnedTimestamp ?? 0) -
                                        (b.pinnedTimestamp ?? 0),
                                );
                        }),
                    );
                }),
                takeUntilDestroyed(),
            )
            .subscribe((communities) =>
                this.pinnedCommunities.set(communities),
            );
    }

    isPinned(data: {
        community: ICommunity;
        pinnedCommunities: ICommunity[];
    }): boolean {
        return data.pinnedCommunities.some((c) => c.id === data.community.id);
    }

    pinCommunity(communityId: number, pinned: boolean): void {
        const previousPinnedCommunitiesIds = this.pinnedCommunities().map(
            (c) => ({ id: c.id, pinnedTimestamp: c.pinnedTimestamp }),
        );
        this.store.dispatch(
            pinCommunity({
                communityId,
                pinned,
                previousPinnedCommunitiesIds,
            }),
        );
    }
}
